/** 多选筛选项：queryParam 为数组。单选是标量，请求里用 resolveSingleSelectForRequest。 */
export function normalizeMultiSelectValue(value: unknown): (string | number)[] {
    if (Array.isArray(value)) {
        return value.filter(v => v !== undefined && v !== null && v !== "");
    }
    if (value === undefined || value === null || value === "") return [];
    return [value as string | number];
}

/** 单选下拉绑定值：从 queryParam 数组取首项 */
export function getSingleSelectModelValue(value: unknown): string | number {
    return normalizeMultiSelectValue(value)[0] ?? "";
}

/** 组合搜索：模糊为 string，精确为 string[] */
export type CombinedSearchValue = string | string[];

export function isCombinedExactValue(value: unknown): value is string[] {
    return Array.isArray(value);
}

export function isCombinedFuzzyValue(value: unknown): value is string {
    return typeof value === "string";
}

/** 请求参数：多选数组，空则省略 */
export function resolveMultiSelectForRequest(values?: (number | string)[]): (number | string)[] | undefined {
    const normalized = normalizeMultiSelectValue(values);
    return normalized.length ? normalized : undefined;
}

/** 请求参数：单选标量，空则 undefined（兼容历史数组） */
export function resolveSingleSelectForRequest(value: unknown): string | number | undefined {
    if (Array.isArray(value)) return normalizeMultiSelectValue(value)[0];
    if (value === undefined || value === null || value === "") return undefined;
    return value as string | number;
}

/** 请求参数：数值单选，空则 undefined */
export function resolveSingleNumberForRequest(value: unknown): number | undefined {
    const raw = resolveSingleSelectForRequest(value);
    if (raw === undefined) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

/** 请求参数：组合搜索模糊 string / 精确 string[]，空则省略 */
export function resolveCombinedSearchForRequest(value: CombinedSearchValue | undefined): string | string[] | undefined {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) {
        const exact = value.map(v => String(v).trim()).filter(Boolean);
        return exact.length ? exact : undefined;
    }
    const text = value.trim();
    return text || undefined;
}

/**
 * 组合搜索写入请求体（与后端 asin/asins、msku/mskus、sku1p/sku1ps 对齐）：
 * - 模糊 string → 单数字段（字符串）
 * - 精确 string[]（含仅 1 项）→ 复数数字段（数组）
 */
export function assignCombinedSearchParam(target: object, value: CombinedSearchValue | undefined, singularKey: string, pluralKey: string): void {
    const resolved = resolveCombinedSearchForRequest(value);
    if (!resolved) return;
    const record = target as Record<string, unknown>;
    if (Array.isArray(resolved)) {
        record[pluralKey] = resolved;
        return;
    }
    record[singularKey] = resolved;
}
